import { AfterContentChecked, ChangeDetectorRef, Component, HostBinding, Input, OnDestroy, OnInit } from '@angular/core';
import { OrganizationsService } from 'src/app/modules/organizations/services/organizations.service';
import { IHash, MdrDashboardsService } from '../../../services/mdr-dashboards.service';
import { DashboardTable } from '../../../interfaces/mdr-dashboards-table-data.interface';
import { ColumnWidth, TableDataSource } from '../../../interfaces/mdr-dashboards-data-source.interface';
import { DataResponse, ProgressBarResponse } from '../../../interfaces/mdr-dashboards.data-response.interface';
import { TimeframeService } from '../../../../timeframe/services/timeframe.service';
import { EntityBrowserComponent } from '../../../../../../common-modules/components/components/entity-browser/entity-browser.component';
import { MatTooltip } from '@angular/material/tooltip';
import { ChartAreaDataSource, PieChartDataSource } from '../../../interfaces/mdr-dashboards-chart-area-data.interface';
import { IIndexable } from '../../../interfaces/mdr-dashboards-widgets.interface';
import { Subscription } from 'rxjs';
import { ExternalDataEndpointsInterface, ExternalDataInterface } from '../../../interfaces/mdr-dashboards-drilldown.interface';

interface DashboardTableColumn {
    name: string;
    threshold: number;
    width: string | number;
    defaultWidth: string | number;
}

export const MIN_PERCENTAGE = 0.01;

@Component({
    selector: 'app-mdr-dashboards-table',
    templateUrl: './mdr-dashboards-table.component.html',
    styleUrls: ['./mdr-dashboards-table.component.scss'],
    standalone: false,
})
export class MdrDashboardsTableComponent implements OnInit, OnDestroy, AfterContentChecked {
    @HostBinding('className') componentClass: string;

    /**  dataSource - retrieved data from the server */
    @Input() dataSource: DashboardTable[];

    /**  displayedColumns - columns settings for table */
    @Input() displayedColumns: string[];

    /**  legend - enable/disable legend */
    @Input() legend = false;

    /**  bulletType - set the bullet type for the legend. By default, is square */
    @Input() bulletType = 'square';

    /**  bottomBorder - enable/disable bottom border for widget */
    @Input() bottomBorder = true;

    /**
     * @var element - element object passed from the tab's component.
     * This object will contain specific settings and custom options for the table widget
     */
    @Input() element: TableDataSource | ChartAreaDataSource | PieChartDataSource;

    /**  mocked - enable/disable mocked data */
    @Input() mocked = true;

    /**  rowHoverEffect - enable/disable hover effect for table rows */
    @Input() rowHoverEffect = false;

    /**  isChart - whether the table is inside any chart or not */
    @Input() isChart = false;

    /**  entityBrowser - table entity object */
    @Input() entityBrowser: EntityBrowserComponent<any>;

    /**  parentClassName - import style from parent element */
    @Input() parentClassName = '';

    /**  className - import custom class name for current element */
    /* @var defaultEmptyValue - what to show on empty/error state */
    @Input() defaultEmptyValue = '- -';

    /* @var className - import custom class name for current element */
    @Input() className = '';
    @Input() currentlyDownloading: IHash;
    public downloadingItems: IHash = {};
    public basicColumns: DashboardTableColumn[];
    public hiddenColumns: string[];
    public emptyDataSource: DashboardTable[];
    public disableTooltip = false;
    public hideDrilldownTitle = true;
    public progressBarTotalValues = -1;
    public docSubscription: Subscription;
    public statusOfDownload: Subscription;

    public constructor(
        public organizationService: OrganizationsService,
        public mdrDashboardsService: MdrDashboardsService,
        public timeframeService: TimeframeService,
        public cdr: ChangeDetectorRef,
    ) {
        this.componentClass = 'dashboard-table';
        this.downloadingItems = this.mdrDashboardsService.currentlyDownloading;

        /**
         * name:            name of the column
         * defaultWidth:    the default width of the column
         * width:           desired width if the number of columns is higher than threshold
         * threshold:       max limit for the default width
         */
        this.basicColumns = [
            {
                name: 'result',
                defaultWidth: '30%',
                width: '80%',
                threshold: 3,
            },
            {
                name: 'recommendations',
                defaultWidth: '10%',
                width: '25%',
                threshold: 4,
            },
            {
                name: 'description',
                defaultWidth: '50%',
                width: '80%',
                threshold: 4,
            },
            {
                name: 'category',
                defaultWidth: '20%',
                width: '15%',
                threshold: 4,
            },
            {
                name: 'details',
                defaultWidth: '60%',
                width: '40%',
                threshold: 3,
            },
            {
                name: 'summary',
                defaultWidth: '30%',
                width: '50%',
                threshold: 3,
            },
            {
                name: 'date',
                defaultWidth: '20%',
                width: '20%',
                threshold: 4,
            },
            {
                name: 'source',
                defaultWidth: '20%',
                width: '15%',
                threshold: 4,
            },
            {
                name: 'readable_category',
                defaultWidth: '20%',
                width: '15%',
                threshold: 4,
            },
            {
                name: 'company_name',
                defaultWidth: '20%',
                width: '15%',
                threshold: 4,
            },
            {
                name: 'processed_date',
                defaultWidth: '20%',
                width: '15%',
                threshold: 4,
            },
        ];

        this.hiddenColumns = ['id', 'logo', 'cid', 'cname'];
    }

    public ngOnInit() {
        this.emptyDataSource = this.calculateEmptyLegend(this.element?.no_rows, this.displayedColumns);
        this.statusOfDownload = this.mdrDashboardsService.fileIsDownloadingObs().subscribe((value) => {
            this.downloadingItems[value.index] = value.downloading;
            this.cdr.detectChanges();
        });
    }

    public ngAfterContentChecked() {
        this.cdr.detectChanges();
    }

    public downloadDoc(dataSource: any | null, data: ExternalDataInterface | undefined, index: number, event: MouseEvent) {
        if (
            typeof data === 'undefined' ||
            Number(dataSource[index].value) === 0 || // if the value is 0, we don't have any content to download
            event.button !== 0 // limit download action only on mouse left click
        ) {
            return;
        }

        const endpointObj =
            data.endpoints.find(
                (item: ExternalDataEndpointsInterface) => item.name.toLowerCase() === dataSource[index].name.toLowerCase(),
            ) || '';
        const filename = data.filename;
        const type = data.type;

        if (!this.downloadingItems[index] && endpointObj) {
            this.docSubscription = this.mdrDashboardsService
                .downloadDocumentObs(endpointObj.endpoint, endpointObj.filename, type, index)
                .subscribe((value) => {
                    this.downloadingItems = this.mdrDashboardsService.currentlyDownloading;
                    this.docSubscription.unsubscribe();
                });
        }
    }

    public allowDocumentDownload(element: any, item: any): boolean {
        let value = typeof item.value === 'string' ? Number(item.value.replace(' ', '')) : item.value;

        return <boolean>element?.drilldown?.allowDownloadData && value > 0;
    }

    public calculateProgressBarMaxValue(dataSource: ProgressBarResponse[], index: number): number {
        if (index > 0) {
            if (this.element.columns?.includes('progress') && this.progressBarTotalValues < 0) {
                this.progressBarTotalValues = this.calculateDataSourceTotal('progress', 'value', dataSource);
            }
            const min_value = MIN_PERCENTAGE * this.progressBarTotalValues;

            return this.progressBarTotalValues + min_value;
        } else {
            return this.progressBarTotalValues;
        }
    }

    public getProcessedValue(data: ProgressBarResponse, dataSource: ProgressBarResponse[]): number {
        let item = data.value ?? Number(data.percent?.replace('%', '')) ?? 0;

        if (this.progressBarTotalValues < 0) {
            this.progressBarTotalValues = this.calculateDataSourceTotal('progress', 'value', dataSource);
        }
        const min_value = MIN_PERCENTAGE * this.progressBarTotalValues;

        return item < min_value ? min_value : item;
    }

    public calculateDataSourceTotal(type: string, property: string, dataSource: any) {
        let total = 0;

        switch (type) {
            case 'progress':
                dataSource.forEach((item: ProgressBarResponse) => {
                    total += (item as IIndexable)[property] ?? 0;
                });
                return total;

            default:
                return total;
        }
    }

    /**
     * Enable/disable tooltip for a specific text according to scrollWidth and clientWidth.
     * @param event - mouse event
     * @param tooltip - tooltip object to be shown
     * @param findClosest - closest class or element to look into
     */
    public checkInputClientVisibility(event: MouseEvent, tooltip: MatTooltip, findClosest = '') {
        let target: Element | null = event.target as Element;
        if (findClosest) {
            target = target.closest(findClosest);
        }
        if (target) {
            this.disableTooltip = !(target.scrollWidth > target.clientWidth);
        }
    }

    /**
     *
     * @param data - datasource
     * @param columns - array containing table columns
     * @param rowsNumber - number of rows
     */
    public fillIncompleteDataSource(data: DashboardTable[], columns: string[] | undefined, rowsNumber: number = 0) {
        if (!columns) {
            return;
        }

        let missingRows = rowsNumber - data.length;

        for (let i = 0; i < missingRows; i++) {
            const temp: { [key: string]: any } = {};

            for (let item of columns) {
                temp[item] = item === 'value' ? this.defaultEmptyValue : '...';
            }
            data.push(<DataResponse>temp);
        }

        return data as any;
    }

    /**
     * Function to calculate legend for empty state
     * @param rowsNumber - number of rows
     * @param columns - array containing table columns
     */
    public calculateEmptyLegend(rowsNumber = 0, columns: string[] = []) {
        let data: DashboardTable[] = [];

        for (let i = 0; i < rowsNumber; i++) {
            const temp: { [key: string]: any } = {};

            for (let item of columns) {
                temp[item] = item === 'value' ? this.defaultEmptyValue : '...';
            }
            data.push(<DashboardTable>temp);
        }
        return data;
    }

    /**
     * Function to retrieve fixed column width if exists, otherwise set it to 'auto'
     * @param column - represents a column in the table
     */
    public getColumnWidth(column: string) {
        let item: ColumnWidth[] = [];

        if ('columnsWidth' in this.element) {
            item = this.element.columnsWidth.filter((obj: ColumnWidth) => {
                return obj.column === column;
            });
        }

        return item ? item[0].width : 'auto';
    }

    ngOnDestroy(): void {
        this.cdr.detach();
        this.docSubscription?.unsubscribe();
        this.statusOfDownload.unsubscribe();
    }
}
